import { capitalize } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { LIMIT_PER_PAGE } from "../../Common/Constants";
import { dateFormat } from "../../Common/DateAndTimeFormat";
import { debounce } from "../../Common/debounce";
import { Loader } from "../../Common/Loader";
import { Pagination } from "../../Common/Pagination";
import { Table } from "../../Common/Table";
import useDebounce from "../../Common/useDebounce";
import {
  setPage,
  setTotalItems,
  setTotalPages,
} from "../../Redux/Slice/Common/PaginationSlice";
import { getPlans } from "../../Redux/Slice/Plans/GetPlansListSlice";
import AddIcon from "../../Assets/svgs/AddIcon";
import { getTopUpPlanList } from "../../Redux/Slice/TopUp/GetTopUpPlanListSlice";
import { ConfirmModel } from "../../Model/ConfirmModel";
import { deletePlan } from "../../Redux/Slice/Plans/DeletePlanSlice";
import useSuccessErrorAlert from "../../Hooks/useSuccessErrorAlert";
import { SuccessModel } from "../../Model/SuccessModel";
import { ErrorModel } from "../../Model/ErrorModel";
import { deleteTopUpPlan } from "../../Redux/Slice/Plans/DeleteTopUpPlanSlice";

export const PlanList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(
    location?.hash == "#topup" ? 2 : 1
  );
  const [subscriptionPlansList, setSubscriptionPlansList] = useState([]);
  const [topUpPlansList, setTopUpPlansList] = useState([]);
  const [localItemsPerPage, setLocalItemsPerPage] = useState(LIMIT_PER_PAGE);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    data: null,
  });
  const { successModal, errorModal, showSuccessMessage, showErrorMessage } =
    useSuccessErrorAlert();
  const searchQuery = useDebounce(searchText, 500);

  const setPageList = (plans) => {
    if (Array.isArray(plans)) {
      return plans.map((item, index) => {
        return {
          ...item,
          count: index + 1,
          name: item?.name,
          interval: item?.interval,
          intervalLabel: item?.intervalLabel,
          planType: capitalize(item?.planTypeDetails?.type ?? ""),
          createdAt: dateFormat(item?.createdAt),
          price: item?.price ? item?.price : "",
          numberOfCredits: item?.numberOfCredits,
          id: item?.id,
        };
      });
    }
  };

  const getPlansList = async (page = 1, limit) => {
    setIsLoading(true);
    const api = selectedTab == 1 ? getPlans : getTopUpPlanList;
    const result = await dispatch(api({ searchText, page, limit }));
    let pagination;
    if (selectedTab == 1) {
      pagination = result?.payload?.pagination;
      setSubscriptionPlansList(setPageList(result?.payload?.data));
    } else {
      pagination = result?.payload?.data?.pagination;
      setTopUpPlansList(setPageList(result?.payload?.data?.response));
    }
    await dispatch(setPage(pagination?.currentPage));
    await dispatch(setTotalItems(pagination?.totalItems));
    await dispatch(setTotalPages(pagination?.totalPages));
    setIsLoading(false);
  };

  const onDeletePlan = async () => {
    const planId = confirmModal?.data?.id;
    setConfirmModal({
      visible: false,
      data: null,
    });
    setIsLoading(true);
    const api = selectedTab == 2 ? deleteTopUpPlan : deletePlan;
    const result = await dispatch(api(planId));
    if (api.fulfilled.match(result) && result?.payload?.success === true) {
      setIsLoading(false);
      showSuccessMessage(
        selectedTab == 2
          ? "Top-Up Plan deleted successfully"
          : "Plan deleted successfully!",
        getPlansList
      );
    } else {
      setIsLoading(false);
      showErrorMessage(result?.payload?.error?.message);
    }
  };

  const onclickDeletePlan = (plan) => {
    setConfirmModal({
      visible: true,
      data: plan,
    });
  };

  const debouncedGetUserList = useCallback(debounce(getPlansList, 300), [
    searchQuery,
    selectedTab,
  ]);

  useEffect(() => {
    debouncedGetUserList(1, localItemsPerPage);
  }, [searchQuery, debouncedGetUserList, selectedTab]);

  const onEdit = (row) => {
    navigate(
      (selectedTab == 1 ? "/edit-subscription-plan/" : "/edit-topup-plan/") +
        row?.id
    );
  };

  const columns =
    selectedTab == 1
      ? [
          {
            label: "Plan Name",
            key: "name",
            className: "profile",
          },
          {
            label: "Plan Type",
            key: "planType",
            className: "cellText",
          },
          {
            label: "Creation Date",
            key: "createdAt",
            className: "cellText",
          },
          {
            label: "Plan Price ($)",
            key: "price",
            className: "cellText",
          },
          {
            label: "Credits",
            key: "numberOfCredits",
            className: "cellText",
          },
        ]
      : [
          {
            label: "Plan Name",
            key: "name",
            className: "profile",
          },
          {
            label: "Creation Date",
            key: "createdAt",
            className: "cellText",
          },
          {
            label: "Plan Price ($)",
            key: "price",
            className: "cellText",
          },
          {
            label: "Credits",
            key: "numberOfCredits",
            className: "cellText",
          },
        ];

  return (
    <>
      {isLoading ? <Loader /> : ""}
      {confirmModal.visible ? (
        <ConfirmModel
          onAccept={onDeletePlan}
          onReject={() =>
            setConfirmModal({
              visible: false,
              data: null,
            })
          }
          yes={"Yes"}
          no={"No"}
          title={selectedTab == 2 ? "Delete Topup Plan!" : "Delete Plan!"}
          message={`Are you sure you want to delete the ${
            selectedTab == 2 ? "Topup" : "Plan"
          }?`}
        />
      ) : null}
      {successModal?.visible ? (
        <SuccessModel message={successModal?.message} />
      ) : null}
      {errorModal?.visible ? (
        <ErrorModel message={errorModal?.message} />
      ) : null}
      <div className="page__body">
        <div className="tabsWrap">
          <ul className="tabs">
            <li
              className={
                selectedTab === 1
                  ? "tabs__link tabs__link--selected"
                  : "tabs__link"
              }
              onClick={() => {
                setSelectedTab(1);
                navigate("#subscription");
              }}
            >
              Subscription Plans
            </li>
            <li
              className={
                selectedTab === 2
                  ? "tabs__link tabs__link--selected"
                  : "tabs__link"
              }
              onClick={() => {
                setSelectedTab(2);
                navigate("#topup");
              }}
            >
              Topup Plans
            </li>
          </ul>
        </div>
        <div className="tabsContent">
          <div className="tabsContent__content tabsContent__content--selected">
            <div className="tableWrap">
              <div className="tableWrap__head tableWrap__head--wrap">
                <div className="formRow m_0  formRow--flexEnd">
                  <div className="formCol formCol--flexInput">
                    <div className="formCol flexField">
                      <input
                        className="form__input form__input--search"
                        type="text"
                        placeholder="Search a Plan Name"
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value.trimStart());
                        }}
                      />
                    </div>
                    {/* <button className="btn btn__white">
                  <svg
                    width="20"
                    height="14"
                    viewBox="0 0 20 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.52637 7H15.5264M1.77637 1.5H18.2764M7.27637 12.5H12.7764"
                      stroke="#475569"
                      stroke-width="1.837"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Filter
                </button> */}
                  </div>
                  {/* <div className="formCol formCol--flex">
                <label for="date" className="form__label">
                  Date
                </label>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                        <DateSelector
                          name="startDate"
                          value={dateRange.startDate}
                          onChange={handleDateChange}
                          placeholder="DD/MM/YYYY"
                        />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <DateSelector
                        name="endDate"
                        value={dateRange.endDate}
                        onChange={handleDateChange}
                        placeholder="DD/MM/YYYY"
                      />
                    </div>
                  </div>
                </div>
              </div> */}
                </div>
                <div className="btn__group">
                  {selectedTab == 1 ? (
                    <button
                      className="btn btn__primary"
                      onClick={() => {
                        navigate("/plan-types");
                      }}
                    >
                      View Plan Types
                    </button>
                  ) : null}
                  <button
                    className="btn btn__primary"
                    onClick={() => {
                      navigate(
                        selectedTab == "1"
                          ? "/add-subscription-plan"
                          : "/add-topup-plan"
                      );
                    }}
                  >
                    <AddIcon />
                    Add{selectedTab == 2 ? " Topup" : ""} Plan
                  </button>
                </div>
              </div>
              <Table
                columns={columns}
                data={selectedTab == 1 ? subscriptionPlansList : topUpPlansList}
                onManagePlan={onEdit}
                onDelete={onclickDeletePlan}
                hasAction="true"
              />
            </div>
          </div>
        </div>
      </div>
      <Pagination
        apiMethod={getPlansList}
        itemsPerPage={localItemsPerPage}
        setItemsPerPage={setLocalItemsPerPage}
      />
    </>
  );
};
